import type { ReactNode } from "react";

interface Props {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  /** Render inside an existing panel/section; otherwise wraps in `.empty-state` shell. */
  inline?: boolean;
}

/** Empty-data placeholder with optional CTA. Compact for inline use; bigger as a page-level empty. */
export function EmptyState({ title, description, icon, actions, inline }: Props) {
  if (inline) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-dashed border-border px-4 py-3" role="status">
        {icon && (
          <div className="shrink-0 text-2xl text-muted-foreground" aria-hidden="true">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <strong className="text-sm font-semibold text-foreground">{title}</strong>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
    );
  }
  return (
    <div
      className="flex flex-col items-center rounded-lg border border-dashed border-border px-6 py-12 text-center"
      role="status"
    >
      {icon && (
        <div className="mb-3 text-4xl text-muted-foreground" aria-hidden="true">
          {icon}
        </div>
      )}
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {description && <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>}
      {actions && <div className="mt-4 flex gap-2">{actions}</div>}
    </div>
  );
}
